import { DrivableCameraInstance, renderBeat, uiBeat, useDisposable } from '@kosmod/graphics-3d';
import { Animation, clamp, ClientData, getAnimationPoint, useAnimatedNumber, useAsync, useBeat, useClientData, useExternalRef, useJeffect, useLogger } from '@kosmod/kit';
import { Component, h, primitiveNodeTypes, ReadOnlyRef, Ref, useEffect, useMemo, useRef, useState } from '@lukekaalim/act';
import { a3 } from '@lukekaalim/act-three';
import * as three from 'three';
import { TheatreState } from '../../hooks/useTheatreState';
import { TheatreSelectionController } from '../../hooks/useTheatreSelection';
import { Logic, Mini, MiniAnimation, MiniID, TheatrePosition } from '../../defs';
import { html, HTML } from '@lukekaalim/act-web';
import { assetsURLs } from '../../assets';
import { Flag, FlagTransition, MonsterFlag } from './Flag';
import { GameAssetClient } from '@kosmod/assets';
import { ReadonlyChunkMap } from '@kosmod/stew';
import { QuickSpriteGraphicRenderer } from './Mini/QuickSprite';
import { MiniGraphicState } from './Mini/graphics';
import { MiniModelGraphicRenderer } from './Mini/Model';
import { createListAnimationHook } from '@kosmod/kit/list';
import { MiniGameMatRenderer } from './Mini/GameMat';


/**
 * This should eventually be configurable, but hardcode it to 400ms for now
 */
const MINI_ANIM_POINT_MS = 400;
const MINI_ANIM_HOP_HEIGHT = 0.25;

export const calculateMiniAnimationPoint = (
  position: TheatrePosition,
  animation: null | MiniAnimation,
  now: DOMHighResTimeStamp,
) => {

  if (!animation || !animation.points.length)
    return [TheatrePosition.copyToVector3(position), position] as const;

  const msSinceStart = Math.max(0, Math.min(now - animation.start, (animation.points.length - 1) * MINI_ANIM_POINT_MS));


  const pointFractionalIndex = msSinceStart / MINI_ANIM_POINT_MS;
  const pointMinIndex = Math.floor(pointFractionalIndex);
  const pointMaxIndex = Math.ceil(pointFractionalIndex);
  // a value from zero to one of the current progress between min/max;
  const pointProgress = pointFractionalIndex - pointMinIndex;

  const pointMin = TheatrePosition.copyToVector3(animation.points[pointMinIndex]);
  const pointMax = TheatrePosition.copyToVector3(animation.points[pointMaxIndex]);

  const output = new three.Vector3().lerpVectors(pointMin, pointMax, Math.cos((pointProgress * Math.PI) + Math.PI) / 2 + 0.5);
  //const output = new three.Vector3().lerpVectors(pointMin, pointMax, pointProgress);
  output.y += Math.sin(pointProgress * Math.PI) * MINI_ANIM_HOP_HEIGHT;

  return [output, animation.points[pointMinIndex]] as const;
}

export type MiniRendererProps = {
  state: TheatreState,

  mini: Mini,
}

const useBinaryAnimation = createListAnimationHook<boolean>(() => 'yes')


export const MiniRenderer: Component<MiniRendererProps> = ({ state, mini }) => {
  const selection = state.selection;
  const isGM = state.data.players[0].id === state.data.self.id;

  const isSelected = selection.target.type === 'mini' && selection.target.miniId === mini.id;
  const isHover = selection.hover.type === 'mini' && selection.hover.miniId === mini.id;
  const someoneElseSelected = selection.target.type !== 'none';

  const selectionAnim = useAnimatedNumber(isSelected ? 1 : isHover ? 0.5 : someoneElseSelected ? -0.1 : 0);

  const logger = useLogger('MiniRenderer');

  const surfaceData = state.surfaceData.get(mini.position.surfaceId);

  const graphicState = useMemo((): MiniGraphicState => {
    return {
      position: mini.position,
      movementAnimation: mini.animation,
      selectionAnimation: selectionAnim,

      hasGodVision: isGM,
      cameraRef: state.camera,
      visibilityChunks: surfaceData && surfaceData.visibility || null,

      // every mini speaks all at once right now
      volumeRef: state.concierge.audio.localVoiceVolume,
    }
  }, [
    isGM,
    mini.position,
    mini.animation,
    selection,
    state.camera,
    //state.data.visibility,
    state.concierge.audio.localVoiceNode,
    surfaceData,
  ]);


  const ref = useRef<three.Sprite | null>(null);

  const onUI = uiBeat.useBeat();

  const flagRef = useRef<HTMLElement | null>(null);

  const flags = useBinaryAnimation(isSelected || isHover ? [true] : [])
  useJeffect(j => {
    const flag = flagRef.get();
    const sprite = ref.get();
    const camera = state.camera.get()!.object!;

    if (!flag || !sprite)
      return;

    j.add(onUI(() => {
      const worldPosition = sprite.getWorldPosition(new three.Vector3());
      worldPosition.add({ x: 0, y: 1.5, z: 0 });
      const screenPos = worldPosition.project(camera);
      
      const x = ((screenPos.x *  .5 + .5) * state.graphics.canvas.get()!.clientWidth);
      const y = ((screenPos.y * -.5 + .5) * state.graphics.canvas.get()!.clientHeight) - 100;

      flag.style.transform = `translate(${x}px, ${y}px)`;
    }))
  }, [flags]);

  const character = Logic.find('character', mini.logic);// mini.logic.find(l => l.key === 'character');
  const hitpoints = Logic.find('hitpoints', mini.logic);
  const monster = Logic.find('monster', mini.logic);

  const renderLogic = Logic.find('render', mini.logic);
  const textureLoader = useRef(() => new three.TextureLoader()).get();



  // logger.log(`RenderLogic`, renderLogic)

  return [
    !!renderLogic && [
      renderLogic.data.renderType === 'quick-sprite' &&
        h(QuickSpriteGraphicRenderer, {
          graphicState,
          quickSprite: renderLogic,
          resources: state.sceneRes,
          textureLoader,
          ref 
        }),
      renderLogic.data.renderType === 'model' && renderLogic.data.modelId &&
        h(MiniModelGraphicRenderer, {
          res: state.sceneRes,
          miniModelId: renderLogic.data.modelId,
          mini,
          ref 
        }),
      renderLogic.data.renderType === 'game-mat' &&
        h(MiniGameMatRenderer, { render: renderLogic.data, res: state.sceneRes, graphics: graphicState, mini, ref })
    ],
    h(primitiveNodeTypes.null, {}, h(HTML, { attach: state.containerRef.get()! }, [
      flags.map(() => {
        return h(html.div, { ref: flagRef, style: { position: 'absolute', pointerEvents: 'none' } }, [
          h(html.section, { style: { transform: `translate(-50%, -50%)` } },
            h(FlagTransition, { visible: isSelected || isHover },
              h(MonsterFlag, { mini, hitpoints, character, isGM, detailed: isSelected, monster })
            )
          )
        ])
      }),
    ]))
  ];
};

export const SceneHTML: Component<{ state: TheatreState, position: three.Vector3 }> = ({ state, children, position }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  
  const logger = useLogger('SceneHTML');

  const onUI = uiBeat.useBeat();
  useJeffect(j => {
    const htmlRef = ref.get();
    const camera = state.camera.get()!.object!;

    if (!htmlRef)
      return;

    j.add(onUI(() => {
      const screenPos = position.clone().project(camera);
      
      const x = ((screenPos.x *  .5 + .5) * state.graphics.canvas.get()!.clientWidth);
      const y = ((screenPos.y * -.5 + .5) * state.graphics.canvas.get()!.clientHeight);

      htmlRef.style.transform = `translate(${x}px, ${y}px)`;
    }))
  }, [position]);

  return h(primitiveNodeTypes.null, {}, h(HTML, { attach: state.containerRef.get()! },
    h(html.div, { ref, style: { position: 'absolute', pointerEvents: 'none' } },
      children)
  ))
}

export const calcMiniPosition = (
  spacePosition: TheatrePosition,

  selectionAnim: Animation<number>,
  animation: MiniAnimation | null,
  camera: DrivableCameraInstance,

  localVoiceVolume: number,

  now: DOMHighResTimeStamp,
) => {
  const value = getAnimationPoint(selectionAnim, now);

  //sprite.scale.set(texture.width/texture.height, 1, 0).multiplyScalar(Math.max((value * 0.05) + 1, 1))

  const selection = new three.Vector2(Math.max(0, (value * 0.05)) + 0);

  const rotationOffset = new three.Vector2(
    camera.forward.x * camera.vertical * 0.1,
    camera.forward.y * camera.vertical * 0.1
  ).clampLength(0, 0.5)

  const ANIM_TIME = 400;

  if (animation && now < animation.start + (animation.points.length * ANIM_TIME)) {
    const elapsed = now - animation.start;
    const frame = Math.floor(elapsed / ANIM_TIME);
    const frameProgress = (elapsed % ANIM_TIME) / ANIM_TIME;

    const current = animation.points[frame] || spacePosition;
    const next = animation.points[frame + 1] || spacePosition;

    const currentV = new three.Vector2(current.vector.x, current.vector.y);
    const nextV = new three.Vector2(next.vector.x, next.vector.y);

    const animatedPosition = currentV.clone().lerp(nextV, Math.sin(frameProgress * Math.PI/2))

    return new three.Vector3(
      animatedPosition.x + 0.5 + rotationOffset.x,
      selection.y + (Math.sin(frameProgress * Math.PI) / 4),
      animatedPosition.y + 0.5 + rotationOffset.y
    )

  } else {
    return new three.Vector3(
      spacePosition.vector.x + 0.5 + rotationOffset.x,
      selection.y + (localVoiceVolume / 5),
      spacePosition.vector.y + 0.5 + rotationOffset.y,
    );
  }
}


export type MiniClientRendererProps = {
  state: TheatreState,

  client: ClientData<Mini>,
}
export const MiniClientRenderer: Component<MiniClientRendererProps> = ({ client, state }) => {
  const mini = useClientData(client);

  return h(MiniRenderer, { mini, state });
}